import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  memo,
} from 'react';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash/debounce';
import L from 'leaflet';
import Input from '@lib/components/Input/Input';
import { NOMINATIM_BASE } from './constants';
import styles from './Map.module.scss';
import './leaflet.scss';
import './leaflet.draw.scss';

function SearchAutocomplete({
  map,
  featureGroupRef,
  onAddMarker,
}: {
  map: L.Map;
  featureGroupRef: React.RefObject<L.FeatureGroup | null>;
  onAddMarker: (coords: { lat: number; lon: number }) => boolean;
}) {
  const { t, i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Toggle map interactions when hovering the suggestions popup
  const setMapLocked = useCallback(
    (locked: boolean) => {
      if (!map) return;
      if (locked) {
        map.scrollWheelZoom.disable();
        map.dragging.disable();
        map.doubleClickZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
      } else {
        map.scrollWheelZoom.enable();
        map.dragging.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();
        map.keyboard.enable();
      }
    },
    [map],
  );

  const searchPlace = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const url = `${NOMINATIM_BASE}/search?format=json&limit=5&q=${encodeURIComponent(query)}&accept-language=${encodeURIComponent(i18n.language)}`;
        const response = await fetch(url, {
          headers: {
            Accept: 'application/json',
            'Accept-Language': i18n.language,
          },
        });
        const data = await response.json();
        setSuggestions(data);
      } catch (error) {
        console.error('Error searching places:', error);
      }
    },
    [i18n.language],
  );

  const debouncedSearchPlace = useMemo(
    () =>
      _debounce((query: string) => {
        searchPlace(query);
        if (query.trim()) {
          setShowSuggestions(true);
        }
      }, 1000),
    [searchPlace],
  );

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    setSearchValue(value);
    debouncedSearchPlace(value);
  };

  const handleSuggestionClick = useCallback(
    (suggestion: any) => {
      setSearchValue(suggestion.display_name);
      setShowSuggestions(false);
      const lat = parseFloat(suggestion.lat);
      const lon = parseFloat(suggestion.lon);
      const added = onAddMarker({ lat, lon });
      if (!added) return;
      const group = featureGroupRef.current;
      if (!group) return;
      const bounds = group.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 });
      }
      setSearchValue('');
    },
    [map, featureGroupRef, onAddMarker],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'Tab' && showSuggestions && suggestions.length > 0) {
        // When Tab is pressed with suggestions visible, focus first suggestion
        e.preventDefault();
        const firstSuggestion = suggestionsRef.current?.querySelector(
          '[role="button"]',
        ) as HTMLElement;
        if (firstSuggestion) {
          firstSuggestion.focus();
        }
      }
    },
    [showSuggestions, suggestions.length],
  );

  const handleClickOutside = (e: MouseEvent) => {
    if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
      setShowSuggestions(false);
    }
  };

  // Stop propagation of wheel/click/touch events from the suggestions container to the map
  useEffect(() => {
    const el = suggestionsRef.current;
    if (!el) return undefined;

    // Use Leaflet utilities to stop propagation to the map
    L.DomEvent.disableScrollPropagation(el);
    L.DomEvent.disableClickPropagation(el);

    // Also guard with native listeners for completeness
    const stop = (e: Event) => {
      e.stopPropagation();
    };
    el.addEventListener('wheel', stop, { passive: false });
    el.addEventListener('touchmove', stop, { passive: false });

    return () => {
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchmove', stop);
    };
  }, [suggestionsRef]);

  // Ensure the map is unlocked if the suggestions panel closes while hovered
  useEffect(() => {
    if (!showSuggestions) setMapLocked(false);
  }, [showSuggestions, setMapLocked]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={searchRef} className={styles.searchContainer}>
      <Input
        autoComplete="off"
        placeholder={t('search-for-places')}
        value={searchValue}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        leftIcon="search"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className={styles.suggestions}
          onMouseEnter={() => setMapLocked(true)}
          onMouseLeave={() => setMapLocked(false)}
          onTouchStart={() => setMapLocked(true)}
          onTouchEnd={() => setMapLocked(false)}
          onWheel={(e) => {
            // Prevent map zoom on trackpad/scroll inside dropdown
            e.stopPropagation();
          }}
        >
          <div>
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                className={styles.suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSuggestionClick(suggestion);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {suggestion.display_name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(SearchAutocomplete);
